import { useState } from 'react';
import { Wallet } from 'lucide-react';
import toast from 'react-hot-toast';

import { Button } from '../../components/atoms/button/button';
import { EmptyState } from '../../components/molecules/empty-state/empty-state';
import { ErrorState } from '../../components/molecules/error-state/error-state';
import { Pagination } from '../../components/molecules/pagination/pagination';
import { useSaques } from '../../hooks/use-saques';
import { ConfirmSaqueModal } from './components/confirm-saque-modal';
import { RefuseSaqueModal } from './components/refuse-saque-modal';
import { SaqueDetailDrawer } from './components/saque-detail-drawer';
import { SaquesCards } from './components/saques-cards';
import { SaquesFiltersBar } from './components/saques-filters';
import { SaquesTable } from './components/saques-table';
import {
  CardsWrapper,
  Header,
  Page,
  SkeletonRow,
  Subtitle,
  TableWrapper,
  Title,
} from './saques.styles';
import { confirmSaqueThunk, refuseSaqueThunk } from '../../store/slices/saques.slice';

export const SaquesPage = () => {
  const {
    items,
    page,
    limit,
    total,
    totalPages,
    filters,
    status,
    error,
    setFilters,
    clearFilters,
    setPage,
    setLimit,
    refresh,
    selectedSaqueId,
    selectedDetail,
    detailStatus,
    detailError,
    openDrawer,
    closeDrawer,
    confirm,
    refuse,
    mutating,
    mutationError,
    clearMutationError,
  } = useSaques();

  const [confirmModal, setConfirmModal] = useState(false);
  const [refuseModal, setRefuseModal] = useState(false);

  const handleConfirm = async () => {
    if (!selectedDetail) return;
    const result = await confirm(selectedDetail.id);
    if (confirmSaqueThunk.fulfilled.match(result)) {
      toast.success('Saque confirmado com sucesso');
      setConfirmModal(false);
      closeDrawer();
    }
  };

  const handleRefuse = async () => {
    if (!selectedDetail) return;
    const result = await refuse(selectedDetail.id);
    if (refuseSaqueThunk.fulfilled.match(result)) {
      toast.success('Saque recusado');
      setRefuseModal(false);
      closeDrawer();
    }
  };

  const isLoading = status === 'loading';
  const isEmpty = !isLoading && status !== 'error' && items.length === 0;
  const hasError = status === 'error';

  return (
    <Page>
      <Header>
        <Title>Saques</Title>
        <Subtitle>
          Gerencie solicitações de saque do time financeiro/suporte. Confirme ou
          recuse pedidos pendentes ou em análise.
        </Subtitle>
      </Header>

      <SaquesFiltersBar
        initialValues={filters}
        onApply={setFilters}
        onClear={clearFilters}
      />

      {isLoading && (
        <div data-testid="saques-loading">
          <SkeletonRow />
          <SkeletonRow />
          <SkeletonRow />
          <SkeletonRow />
          <SkeletonRow />
        </div>
      )}

      {hasError && (
        <ErrorState
          title="Não foi possível carregar os saques"
          description={error ?? 'Tente novamente em alguns instantes.'}
          action={
            <Button variant="secondary" onClick={() => refresh()}>
              Tentar novamente
            </Button>
          }
        />
      )}

      {isEmpty && (
        <EmptyState
          icon={<Wallet size={26} />}
          title="Nenhum saque encontrado"
          description="Tente ajustar os filtros ou aguarde novas solicitações chegarem."
          action={
            <Button variant="secondary" onClick={clearFilters}>
              Limpar filtros
            </Button>
          }
        />
      )}

      {!isLoading && !hasError && items.length > 0 && (
        <>
          <TableWrapper>
            <SaquesTable items={items} onOpenDetail={openDrawer} />
          </TableWrapper>
          <CardsWrapper>
            <SaquesCards items={items} onOpenDetail={openDrawer} />
          </CardsWrapper>
          <Pagination
            page={page}
            limit={limit}
            total={total}
            totalPages={totalPages}
            onPageChange={setPage}
            onLimitChange={setLimit}
          />
        </>
      )}

      <SaqueDetailDrawer
        isOpen={selectedSaqueId !== null}
        detail={selectedDetail}
        status={detailStatus}
        errorMessage={detailError}
        onClose={() => {
          if (mutating) return;
          closeDrawer();
          clearMutationError();
        }}
        onConfirm={() => {
          clearMutationError();
          setConfirmModal(true);
        }}
        onRefuse={() => {
          clearMutationError();
          setRefuseModal(true);
        }}
      />

      <ConfirmSaqueModal
        isOpen={confirmModal}
        saque={selectedDetail}
        loading={mutating?.action === 'confirm'}
        errorMessage={mutating?.action === 'confirm' ? null : mutationError}
        onConfirm={handleConfirm}
        onClose={() => {
          if (mutating?.action === 'confirm') return;
          setConfirmModal(false);
          clearMutationError();
        }}
      />

      <RefuseSaqueModal
        isOpen={refuseModal}
        saque={selectedDetail}
        loading={mutating?.action === 'refuse'}
        errorMessage={mutating?.action === 'refuse' ? null : mutationError}
        onConfirm={handleRefuse}
        onClose={() => {
          if (mutating?.action === 'refuse') return;
          setRefuseModal(false);
          clearMutationError();
        }}
      />
    </Page>
  );
};
